import { CompoundEntityRef } from '@mainstage/catalog-model';
import { z } from 'zod';
import { Brand } from './utils';

export enum WitboostCommercialModule {
  ControlPlane = 'ControlPlane',
  MarketPlane = 'MarketPlane',
  ComputationalGovernance = 'ComputationalGovernance',
  Witty = 'Witty',
}

export const ControlPlaneTopics = new Set([
  'builder',
  'catalog',
  'coordinator',
  'governance',
  'platform.access-token-generated',
  'platform.access-token-revoked',
  'platform.access-token-rotated',
  'platform.service-account-registered',
  'platform.service-account-unregistered',
  'settings.assign-role',
  'settings.change-role-permissions',
  'settings.create-new-role',
  'settings.custom-view-management',
  'settings.extension-manager-navigated',
  'settings.platform-settings-navigated',
  'settings.rbac-panel-navigated',
  'settings.tech-adapter-deleted',
  'settings.tech-adapter-registered',
  'settings.tech-adapter-updated',
]);

export enum WitboostModule {
  BUILDER = 'builder',
  CGP = 'cgp',
  MARKETPLACE = 'marketplace',
  PROVISIONING = 'provisioning',
  ADMIN = 'admin',
  WITTY = 'witty',
}

export function getLicensedModules(apiResponse: {
  // This should reflect the new license model
  license: {
    modules: string[];
    licensedUsers?: string;
  };
}): WitboostModule[] {
  const modules = new Set<WitboostModule>();
  for (const module of apiResponse.license.modules) {
    switch (module) {
      case WitboostCommercialModule.ControlPlane:
        modules.add(WitboostModule.BUILDER);
        modules.add(WitboostModule.PROVISIONING);
        modules.add(WitboostModule.CGP);
        modules.add(WitboostModule.ADMIN);
        break;
      case WitboostCommercialModule.MarketPlane:
        modules.add(WitboostModule.MARKETPLACE);
        modules.add(WitboostModule.ADMIN);
        break;
      case WitboostCommercialModule.ComputationalGovernance:
        modules.add(WitboostModule.CGP);
        modules.add(WitboostModule.ADMIN);
        break;
      case WitboostCommercialModule.Witty:
        modules.add(WitboostModule.WITTY);
        modules.add(WitboostModule.ADMIN);
        break;
      default:
        break;
    }
  }
  return Array.from(modules);
}

export type SerializedBuffer = { type: 'Buffer'; data: number[] };

export const userRefSchema = z.string().refine(s => s.startsWith('user:'));
export type UserEntityRef = Brand<
  z.infer<typeof userRefSchema>,
  'UserEntityRef'
>;

export function isUserEntityRef(ref: string): boolean {
  return userRefSchema.safeParse(ref).success;
}

export const groupRefSchema = z.string().refine(s => s.startsWith('group:'));
export type GroupEntityRef = Brand<
  z.infer<typeof groupRefSchema>,
  'GroupEntityRef'
>;

export const systemRefSchema = z.string().refine(s => s.startsWith('system:'));
export type SystemEntityRef = Brand<
  z.infer<typeof systemRefSchema>,
  'SystemEntityRef'
>;

export type EnvironmentExternal = {
  /**
   * of the external environment
   */
  id: number;
  name: string;
};

export type Environment = {
  name: string;
  /**
   * the smaller is the number, the more is prioritized.
   */
  priority: number;
};

/**
 * This type is defined here because it is used by multiple plugins.
 * To avoid replicating multiple times, we defined it here, preventing circular dependencies.
 */
export enum NotificationType {
  QUESTION = 'Question',
  ACCESS_REQUEST = 'AccessRequest',
  INFORMATIVE = 'Informative',
  ACCESS_RESPONSE = 'AccessResponse',
  REVOKE_REQUEST = 'RevokeRequest',
  REVOKE_RESPONSE = 'RevokeResponse',
  APPROVAL_REQUEST = 'ApprovalRequest',
  APPROVAL_RESPONSE = 'ApprovalResponse',
}

export type TypedCompoundEntityRef<Kind extends string> = CompoundEntityRef & {
  kind: Kind;
};

export type ProxyResponse<T> = {
  httpStatus: number;
  body: T;
  requestBody?: any;
};
